import { ColliUserClient } from '@frontend/colli/api';
import { Colli, ColliUser } from '@frontend/colli/types';
import { EntityType, formatDateTimeWithLocale } from '@frontend/common';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { EventLevel, EventType } from '@frontend/events/types';
import { LabelClient } from '@frontend/label/api';
import { Label } from '@frontend/label/types';
import { CommonMessage, useLocale } from '@frontend/lang';
import { HistoryEventDetails, TransactionHistory, getHistoryEventTitleClassName, mapEventDataToDisplay } from '@frontend/transaction/common';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { PlatformEventReferenceBadges } from '../../modal/platform-event-reference-badges/platform-event-reference-badges.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { useAppDispatch } from '../../redux/store';
import { PrintModal } from '../../transaction/modal/print-modal/print-modal';
import { buildSlotParentIds } from '../../transaction/modal/transaction-detail-slot-parents';
import { useColliDetailModal } from './use-colli-detail-modal';

export const ColliGeneralSection = ({ colli }: { colli: Colli }) => (
    <div className='inline-flex w-full items-start justify-start gap-6 self-stretch'>
        <div className='bg-background-surface outline-border-default inline-flex flex-1 flex-col items-start justify-start gap-3 rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>
                <FormattedMessage
                    id='transaction.detail-modal.current-location'
                    defaultMessage='Current location'
                />
            </div>
            <div className='inline-flex flex-wrap items-center gap-2'>
                <PlatformObjectBadge
                    id={colli.current_spot_id ?? undefined}
                    entityType={EntityType.SPOT}
                />
                {colli.current_slot_id && (
                    <PlatformObjectBadge
                        id={colli.current_slot_id}
                        entityType={EntityType.SLOT}
                        parentIds={buildSlotParentIds({ spotId: colli.current_spot_id, moduleId: colli.current_module_id })}
                    />
                )}
            </div>
        </div>
        <div className='bg-background-surface outline-border-default inline-flex flex-1 flex-col items-start justify-start gap-3 rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>
                <FormattedMessage
                    id='transaction.detail-modal.destination'
                    defaultMessage='Destination'
                />
            </div>
            <div className='inline-flex flex-wrap items-center gap-2'>
                <PlatformObjectBadge
                    id={colli.spot_id ?? undefined}
                    entityType={EntityType.SPOT}
                />
                {colli.slot_id && (
                    <PlatformObjectBadge
                        id={colli.slot_id}
                        entityType={EntityType.SLOT}
                        parentIds={buildSlotParentIds({ spotId: colli.spot_id, moduleId: colli.module_id })}
                    />
                )}
            </div>
        </div>
    </div>
);

export const ColliLabelsSection = ({ colli }: { colli: Colli }) => {
    const dispatch = useAppDispatch();
    const viewProps = useColliDetailModal();
    return (
        <ReactiveObjectList<Label>
            key='colli-labels-list'
            fetch={(args) => LabelClient.fetchLabels({ ...args, colli_id: colli.id, account_id: colli.account_id })}
            dispatch={dispatch}
            columns={{
                columns: ['language', 'width', 'height'],
                headerOverwrite: new Map([
                    ['width', CommonMessage.OBJECTS.DIMENSION.WIDTH],
                    ['height', CommonMessage.OBJECTS.DIMENSION.HEIGHT]
                ]),
                customActionButtons: [
                    {
                        label: (
                            <FormattedMessage
                                id='labels.previewPdf'
                                defaultMessage='Preview PDF'
                            />
                        ),
                        onClick: (label) => {
                            LabelClient.fetchPdfPreview(label.account_id, label.id).then((blob) => {
                                window.open(window.URL.createObjectURL(blob), '_blank');
                            });
                        }
                    },
                    {
                        label: (
                            <FormattedMessage
                                id='labels.previewHtml'
                                defaultMessage='Preview HTML'
                            />
                        ),
                        onClick: (label) => {
                            LabelClient.fetchHtmlPreview(label.account_id, label.id).then((blob) => {
                                window.open(window.URL.createObjectURL(blob), '_blank');
                            });
                        }
                    },
                    {
                        label: (
                            <FormattedMessage
                                id='labels.printLabel'
                                defaultMessage='Print label'
                            />
                        ),
                        onClick: (label) =>
                            viewProps.openModal('print-label-modal', () => (
                                <PrintModal
                                    id='print-label-modal'
                                    onCancel={() => viewProps.closeModal('print-label-modal')}
                                    dispatch={dispatch}
                                    label={label}
                                />
                            ))
                    }
                ]
            }}
            type={EntityType.LABEL}
        />
    );
};

export const ColliUsersSection = ({ colli }: { colli: Colli }) => {
    const dispatch = useAppDispatch();
    return (
        <ReactiveObjectList<ColliUser>
            key='colli-users-list'
            fetch={(args) => ColliUserClient.fetchColliUsers({ ...args, colli_id: colli.id })}
            dispatch={dispatch}
            columns={{
                columns: ['user_id', 'type', 'creation_timestamp'],
                headerOverwrite: new Map([['user_id', CommonMessage.OBJECTS.USER.DEFAULT]])
            }}
            type={EntityType.USER}
            showCount
        />
    );
};

export const ColliHistorySection = ({ colli }: { colli: Colli }) => {
    const { locale } = useLocale();
    return (
        <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.HISTORY}</div>
            <TransactionHistory
                key='history'
                entityType={EntityType.COLLI}
                entityId={colli.id}
                queryParams={{
                    level: [EventLevel.INFO, EventLevel.ERROR],
                    type: [EventType.COLLI_HISTORY_LOGGED, EventType.NOTIFICATION_SENT]
                }}
                content={(event) => (
                    <div className='flex flex-col gap-2'>
                        <p className={getHistoryEventTitleClassName(event)}>{mapEventDataToDisplay(event.data)}</p>
                        <HistoryEventDetails
                            event={event}
                            timestamp={formatDateTimeWithLocale(new Date(event.creation_timestamp), locale)}
                        />
                        <PlatformEventReferenceBadges
                            event={event}
                            excludeKeys={['colli_id']}
                        />
                    </div>
                )}
            />
        </div>
    );
};

export const ColliEventsSection = ({ colli }: { colli: Colli }) => {
    const { locale } = useLocale();
    return (
        <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.EVENT_LOGS}</div>
            <TransactionHistory
                key='events'
                entityType={EntityType.COLLI}
                entityId={colli.id}
                showChildren
                queryParams={{ level: [EventLevel.INFO, EventLevel.ERROR] }}
                content={(event) => (
                    <div className='flex flex-col gap-2'>
                        <p className={getHistoryEventTitleClassName(event)}>{mapEventDataToDisplay(event.data)}</p>
                        <HistoryEventDetails
                            event={event}
                            timestamp={formatDateTimeWithLocale(new Date(event.creation_timestamp), locale)}
                        />
                        <PlatformEventReferenceBadges
                            event={event}
                            excludeKeys={['colli_id']}
                        />
                    </div>
                )}
            />
        </div>
    );
};
